import React from 'react'
import { useIntl, IntlShape } from 'react-intl'
import { Table, Column, AutoSizer } from 'react-virtualized'
import { css } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Text } from '@contrast-security-inc/design-system-typography'
import { Checkbox } from '../../legacy-design-system/checkbox'
import { ApplicationModule } from './api/get-application-modules'
import {
  ModalBodyProps,
  SelectModuleProps,
  SelectPrimaryProps
} from './unmerge-dialog'
import { gradeColumnCellRenderer } from './grade-cell'
import { applicationColumnCellRenderer } from './application-cell'
import { routeCoverageColumnCellRenderer } from './route-coverage-cell'
import { hasRole } from 'contrastBase/RoleSwitch'

export const gradeColumnWidth = 100
export const applicationColumnWidth = 300
const applicationColumnMaxWidth = 1000
export const routeCoverageColumnWidth = 180
const fullColumnHeaderMinWidth = 342

const columnCellCss = css`
  margin-left: 11px;
  margin-right: 10px;
`

export const selectAllCheckboxTestId = 'select-all-checkbox-test-id'
export const primaryApplicationCheckboxTestId =
  'primary-application-checkbox-test-id'
export const moduleCheckboxTestId = 'module-checkbox-test-id'

interface ApplicationModulesGridProps extends ModalBodyProps {
  isLoading: boolean
  applicationModules: ApplicationModule[]
  onPrimarySelection: (props: SelectPrimaryProps) => void
  onModuleSelection(props: SelectModuleProps): void
}

export function ApplicationModulesGrid({
  isLoading,
  applicationModules,
  moduleCount,
  ...modalBodyProps
}: ApplicationModulesGridProps) {
  const intl = useIntl()
  const moduleIds = applicationModules
    .filter((module) => !module.primary)
    .map(({ uuid }) => uuid)
  return (
    <div
      className={css`
        background-color: ${theme.color.neutral10};
        padding: 15px 5px 0 20px;
        height: 30vh;
      `}
    >
      <AutoSizer>
        {({ width, height }) => {
          const routeCoverageColumnLabel =
            width >= fullColumnHeaderMinWidth
              ? 'title_unmerge_routesExercised'
              : 'title_unmerge_routesExercised_shortened' // trim header to decongest the look of the table

          return (
            <Table
              width={width}
              height={height}
              headerHeight={35}
              rowHeight={80}
              rowCount={
                isLoading
                  ? getLoadingRowCount(moduleCount)
                  : applicationModules.length
              }
              rowGetter={({ index }) =>
                isLoading ? {} : applicationModules[index]
              }
              headerRowRenderer={({ columns, style }) => (
                <TableHeader
                  isLoading={isLoading}
                  style={style}
                  columns={columns}
                  allModuleIds={moduleIds}
                  moduleCount={moduleCount}
                  {...modalBodyProps}
                />
              )}
              rowRenderer={({ columns, rowData, key, style }) => (
                <RowRenderer
                  rowData={rowData}
                  isLoading={isLoading}
                  key={key}
                  style={style}
                  columns={columns}
                  uuid={rowData.uuid}
                  isPrimaryModule={rowData.primary}
                  allModuleIds={moduleIds}
                  moduleCount={moduleCount}
                  {...modalBodyProps}
                />
              )}
            >
              <Column
                label="title_letterGrade_score"
                dataKey="grade"
                width={gradeColumnWidth}
                headerRenderer={({ label }) =>
                  defaultColumnHeaderRenderer({
                    intl,
                    label
                  })
                }
                cellRenderer={({ rowData }) =>
                  gradeColumnCellRenderer({ isLoading, rowData })
                }
              />
              <Column
                width={applicationColumnWidth}
                maxWidth={applicationColumnMaxWidth}
                flexGrow={5}
                label="title_application"
                dataKey="name"
                className={columnCellCss}
                cellRenderer={({ rowData }) =>
                  applicationColumnCellRenderer({ isLoading, rowData })
                }
                headerRenderer={({ label }) =>
                  defaultColumnHeaderRenderer({
                    intl,
                    label
                  })
                }
              />
              <Column
                width={routeCoverageColumnWidth}
                label={routeCoverageColumnLabel}
                dataKey="routes"
                cellRenderer={({ rowData: { routes } }) =>
                  routeCoverageColumnCellRenderer({ isLoading, routes })
                }
                headerRenderer={({ label }) =>
                  defaultColumnHeaderRenderer({
                    intl,
                    label
                  })
                }
              />
            </Table>
          )
        }}
      </AutoSizer>
    </div>
  )
}

interface RendererProps extends ModalBodyProps {
  isLoading: boolean
  columns: React.ReactNode[]
  style: React.CSSProperties
  allModuleIds: string[]
}

function RowRenderer({
  isLoading,
  columns,
  allSelected,
  moduleSelections,
  uuid,
  isPrimaryModule,
  style,
  onModuleSelection,
  allModuleIds,
  setAllSelected,
  setModuleSelections,
  onPrimarySelection,
  rowData
}: RendererProps & {
  uuid: string
  isPrimaryModule: boolean
  rowData: ApplicationModule
  onModuleSelection(props: SelectModuleProps): void
  onPrimarySelection(props: SelectPrimaryProps): void
}) {
  return (
    <div
      data-testid={isPrimaryModule ? 'primary-application-row' : 'module-row'}
      style={style}
      className={css`
        display: flex;
        flex-direction: row;
        align-items: center;
        height: 75px;
      `}
    >
      <>
        <div
          className={css`
            @media (min-width: 610px) {
              margin-right: 15px;
            }
          `}
        >
          {hasRole(rowData.role, 'EDIT') ? (
            <Checkbox
              label=""
              disabled={isLoading}
              data-testid={
                isPrimaryModule
                  ? primaryApplicationCheckboxTestId
                  : moduleCheckboxTestId
              }
              value={isPrimaryModule ? allSelected : !!moduleSelections[uuid]}
              onChange={() => {
                if (isPrimaryModule) {
                  onPrimarySelection({
                    allModules: allModuleIds,
                    allSelected,
                    setAllSelected,
                    setModuleSelections
                  })
                } else {
                  if (!hasRole(rowData.role, 'EDIT')) {
                    return
                  }
                  onModuleSelection({
                    moduleID: uuid,
                    allSelected,
                    allModules: allModuleIds,
                    moduleSelections,
                    setAllSelected,
                    setModuleSelections
                  })
                }
              }}
            />
          ) : (
            <div
              className={css`
                width: 26px;
                visibility: hidden;
              `}
            >
              {/* This is a hidden element to replace the checkbox for modules that do not have the EDIT role and keep alignment */}
            </div>
          )}
        </div>

        <div
          className={css`
            display: flex;
            align-items: center;
            border-bottom: 1px solid ${theme.color.neutral30};
            width: 92%;
            height: 100%;
          `}
        >
          {columns}
        </div>
      </>
    </div>
  )
}

function TableHeader({
  isLoading,
  columns,
  style,
  allSelected,
  onPrimarySelection,
  allModuleIds,
  setAllSelected,
  setModuleSelections
}: RendererProps & {
  onPrimarySelection: (props: SelectPrimaryProps) => void
}) {
  return (
    <div
      className={css`
        display: flex;
        flex-direction: row;
        align-items: center;
      `}
      role="row"
      style={style}
    >
      <div
        className={css`
          @media (min-width: 610px) {
            margin-right: 15px;
          }
        `}
      >
        <Checkbox
          label=""
          disabled={isLoading}
          value={allSelected}
          data-testid={selectAllCheckboxTestId}
          onChange={() =>
            onPrimarySelection({
              allModules: allModuleIds,
              allSelected,
              setAllSelected,
              setModuleSelections
            })
          }
        />
      </div>
      <div
        className={css`
          display: flex;
          border-bottom: 1px solid ${theme.color.neutral30};
          color: ${theme.color.neutral70};
          align-items: center;
          width: 92%;
          padding-left: 10px;
          padding-bottom: 7px;
        `}
      >
        {columns}
      </div>
    </div>
  )
}

function defaultColumnHeaderRenderer({
  intl,
  label
}: {
  intl: IntlShape
  label: React.ReactNode
}) {
  return (
    <div
      className={css`
        padding-bottom: 2px;
      `}
    >
      <Text font="metadata">{intl.formatMessage({ id: label as string })}</Text>
    </div>
  )
}

// We only want to show the same number of loading rows as the module count
// if the module count is less than 10. Otherwise it's unnecessary to render
// a ton of loading rows past the first scroll.
function getLoadingRowCount(moduleCount: number) {
  return moduleCount > 10 ? 10 : moduleCount
}
